/**
 * passage.jsx — render a shelved passage as what it IS, rather than as one long line.
 *
 * FOUNDER, 2026-08-16, after seven chapters had been brought to depth: the reading surface was
 * flattening every command block into the sentence around it. A learner opening chapter 1 read
 *
 *     Check: ```bash node --version npm --version ``` **You should see** — two version numbers
 *
 * which is not a procedure, it is a paragraph wearing the punctuation of one. The whole value of the
 * depth pass — a command you can copy, and what you should see when you run it — arrived as mush at
 * the only place a learner ever looks. The markdown was correct in the file and correct in the
 * compiled shelf; it was correct everywhere except on screen.
 *
 * The PARSER lives in passage.mjs, with no JSX in it, so gate-passage-render.mjs can run it under
 * plain node and check the one invariant that matters: the letters and digits that come out are
 * exactly the letters and digits that went in. A renderer that reshapes text is also a renderer
 * that can lose it, quietly, in the one place nobody re-reads.
 */
import { blocks, inlineParts } from './passage.mjs';

const Inline = ({ text }) => (
  <>{inlineParts(text).map((part, k) => (
    part.kind === 'code' ? <code key={k} className="pb-code">{part.text}</code>
      : part.kind === 'bold' ? <b key={k}>{part.text}</b>
      : <span key={k}>{part.text}</span>
  ))}</>
);

export default function PassageBody({ text }) {
  return (
    <div className="pb">
      {blocks(text).map((b, i) => {
        if (b.kind === 'pre') return (
          <pre key={i} className="pb-pre">
            {/* The language is shown rather than hidden: a reader who does not know whether he is
                looking at a shell command or its output cannot safely copy either. */}
            {b.lang ? <span className="pb-lang">{b.lang}</span> : null}
            <code>{b.text}</code>
          </pre>
        );
        if (b.kind === 'table') return (
          /* Wrapped, because a wide table must scroll inside itself rather than push the page
             sideways on a phone — which is where most of this course is read. */
          <div key={i} className="pb-tablewrap">
            <table className="pb-table"><tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => (
                  ri === 0 ? <th key={ci}><Inline text={c} /></th> : <td key={ci}><Inline text={c} /></td>
                ))}</tr>
              ))}
            </tbody></table>
          </div>
        );
        if (b.kind === 'list') return (
          <ul key={i} className="pb-list">
            {b.items.map((it, k) => <li key={k}><Inline text={it} /></li>)}
          </ul>
        );
        return <p key={i} className="pb-p"><Inline text={b.text} /></p>;
      })}
    </div>
  );
}
